// AufrufDisplayPage.jsx — Kämpfer-Aufruf Display (Standalone, kein Login)
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import LanguageSwitch from '../components/LanguageSwitch';
import './AufrufDisplayPage.css';
import { wanduhrzeit } from '../lib/wanduhrzeit';

// TDA Live — öffentliche App für Zuschauer, Eltern und Sportler
// (Quelle ~/live-tda). ?t=<id>&tab=suche öffnet direkt die Athletensuche
// dieses Turniers.
const LIVE_URL = 'https://live.tda-intl.org';

// Verzug erst ab 5 Minuten zeigen — darunter ist es Rauschen, und ein
// Schirm, der dauernd "+2 min" meldet, wird nicht mehr gelesen.
const VERZUG_AB_MIN = 5;

function Verzug({ minuten, planzeit, t, gross }) {
  if (minuten == null || Math.abs(minuten) < VERZUG_AB_MIN) return null;
  const spaeter = minuten > 0;
  return (
    <span className={`adp-verzug adp-verzug--${spaeter ? 'spaeter' : 'frueher'}${gross ? ' adp-verzug--gross' : ''}`}
          title={planzeit ? t('aufruf.plan', { zeit: planzeit }) : undefined}>
      {t(spaeter ? 'aufruf.later' : 'aufruf.earlier', { n: Math.abs(minuten) })}
      {gross && planzeit && <span className="adp-verzug-plan"> · {t('aufruf.plan', { zeit: planzeit })}</span>}
    </span>
  );
}

/** "2026-09-25T07:30" → "07:30" bzw. "Fr 25.09." — Text, keine Zeitzone */
const hhmm = (s) => (s ? s.slice(11, 16) : '');
function tagKurz(s) {
  if (!s) return '';
  const [j, m, d] = s.slice(0, 10).split('-').map(Number);
  const wt = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][new Date(j, m - 1, d).getDay()];
  return `${wt} ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.`;
}

/**
 * Infoleiste unten: Einwaage, Hinweise des Veranstalters (wechseln alle 8 s)
 * und der QR-Code zu TDA Live. Gepflegt in /aufruf-control/:turnierId.
 */
function InfoLeiste({ info, turnierId, t }) {
  const [qr, setQr] = useState('');
  const [nr, setNr] = useState(0);
  const liveLink = `${LIVE_URL}/?t=${turnierId}&tab=suche`;

  useEffect(() => {
    QRCode.toString(liveLink, { type: 'svg', margin: 1, errorCorrectionLevel: 'M',
                                color: { dark: '#000000', light: '#ffffff' } })
      .then(setQr).catch(() => setQr(''));
  }, [liveLink]);

  const meldungen = info?.meldungen ?? [];
  useEffect(() => {
    if (meldungen.length < 2) { setNr(0); return undefined; }
    const id = setInterval(() => setNr(n => (n + 1) % meldungen.length), 8000);
    return () => clearInterval(id);
  }, [meldungen.length]);

  const ew = info?.einwaage;
  let einwaage = null;
  if (ew && ew.zustand === 'offen') einwaage = t('aufruf.weigh_open', { bis: hhmm(ew.bis) });
  else if (ew && ew.zustand === 'kommt') {
    einwaage = ew.heute
      ? t('aufruf.weigh_soon', { von: hhmm(ew.von) })
      : t('aufruf.weigh_day', { tag: tagKurz(ew.von), von: hhmm(ew.von), bis: hhmm(ew.bis) });
  }

  return (
    <footer className="adp-info">
      <div className="adp-info-text">
        {einwaage && (
          <div className={`adp-info-einwaage${ew.zustand === 'offen' ? ' offen' : ''}`}>{einwaage}</div>
        )}
        {meldungen.length > 0 ? (
          <div className="adp-info-meldung" key={nr}>
            <span className="adp-info-lbl">{t('aufruf.info')}</span>
            <span className="adp-info-satz">{meldungen[nr % meldungen.length]}</span>
            {meldungen.length > 1 && (
              <span className="adp-info-zaehler">{(nr % meldungen.length) + 1}/{meldungen.length}</span>
            )}
          </div>
        ) : !einwaage && (
          <div className="adp-info-fuss">TDA International · Kämpfer-Aufruf</div>
        )}
      </div>
      {qr && (
        <div className="adp-info-qr">
          <div className="adp-info-qr-text">
            <strong>{t('aufruf.live_qr')}</strong>
            <span>live.tda-intl.org</span>
          </div>
          {/* SVG kommt aus der qrcode-Bibliothek, nicht aus Nutzereingaben */}
          <div className="adp-info-qr-bild" dangerouslySetInnerHTML={{ __html: qr }} />
        </div>
      )}
    </footer>
  );
}

const GURT_COLOR = {
  'Weiß':    '#f0f0f0', 'Gelb':   '#f5d200', 'Orange': '#f08000',
  'Grün':    '#2d8a2d', 'Blau':   '#1a5fa8', 'Rot':    '#cc1a1a',
  'Braun':   '#7a4a1a', 'Schwarz':'#1a1a1a', 'Dan 1':  '#1a1a1a',
};

function rundenName(runde, t) {
  if (runde === 1) return t('aufruf.final');
  if (runde === 2) return t('aufruf.semifinal');
  if (runde === 3) return t('aufruf.quarterfinal');
  if (runde === 4) return t('aufruf.round_of_16');
  return t('aufruf.round', { n: runde });
}

function GurtDot({ farbe }) {
  const col = GURT_COLOR[farbe] ?? '#888';
  return (
    <span
      className="adp-gurt-dot"
      style={{ background: col, border: farbe === 'Weiß' ? '1px solid #555' : 'none' }}
      title={farbe}
    />
  );
}

// Nur anzeigen wenn innerhalb von 2 Stunden — sonst null
function calcCountdownFromMs(diff) {
  if (diff > 7200000) return null;  // > 2h nicht anzeigen
  if (diff <= -90000) return null;
  if (diff <= 0) return { text: 'JETZT', state: 'now' };
  const totalSecs = Math.floor(diff / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  const text = `${mins}:${String(secs).padStart(2, '0')}`;
  if (diff < 120000)  return { text, state: 'urgent' };
  if (diff < 600000)  return { text, state: 'soon' };
  return { text, state: 'ok' };
}

function calcCountdown(isoStr, now) {
  const t = wanduhrzeit(isoStr);
  if (!t) return null;
  return calcCountdownFromMs(t - now);
}

function calcCountdownToTime(timeStr, turnierdatum, now) {
  if (!timeStr || !turnierdatum) return null;
  const datePart = String(turnierdatum).slice(0, 10);
  const target = new Date(`${datePart}T${timeStr}:00`);
  return calcCountdownFromMs(target - now);
}

function getSlotStatus(slot, turnierdatum, now) {
  if (!turnierdatum || !slot.start_time) return 'upcoming';
  const datePart = String(turnierdatum).slice(0, 10);
  const start = new Date(`${datePart}T${slot.start_time}:00`);
  if (now < start.getTime()) return 'upcoming';
  if (slot.end_time) {
    const end = new Date(`${datePart}T${slot.end_time}:00`);
    return now < end.getTime() ? 'active' : 'done';
  }
  return now < start.getTime() + 3600000 ? 'active' : 'done';
}

// ── Kleine Komponenten für die Grid-Ansicht ──────────────────────────────────

function FighterCard({ vorname, nachname, verein, gurt, side }) {
  return (
    <div className={`adp-fighter ${side}`}>
      <div className="adp-fighter-name">
        {vorname} {nachname}
        {gurt && <GurtDot farbe={gurt} />}
      </div>
      <div className="adp-fighter-club">{verein || '—'}</div>
    </div>
  );
}

function KampfRow({ kampf, t }) {
  return (
    <div className="adp-kampf-row">
      <div className="adp-kampf-meta">
        <span className="adp-kampf-runde">{rundenName(kampf.runde, t)}</span>
        <span className="adp-kampf-kat">{kampf.bracket_name}</span>
      </div>
      <div className="adp-kampf-vs">
        {kampf.k1_vorname
          ? <FighterCard vorname={kampf.k1_vorname} nachname={kampf.k1_nachname} verein={kampf.k1_verein} gurt={kampf.k1_gurt} side="left" />
          : <div className="adp-fighter left adp-tbd">—</div>
        }
        <div className="adp-vs-badge">VS</div>
        {kampf.k2_vorname
          ? <FighterCard vorname={kampf.k2_vorname} nachname={kampf.k2_nachname} verein={kampf.k2_verein} gurt={kampf.k2_gurt} side="right" />
          : <div className="adp-fighter right adp-tbd">—</div>
        }
      </div>
    </div>
  );
}

function ZeitplanSlot({ slot, formenByCode, turnierdatum, now, t }) {
  const status = getSlotStatus(slot, turnierdatum, now);
  if (status === 'done') return null;
  const label = slot.start_time
    ? (slot.end_time ? `${slot.start_time}–${slot.end_time}` : slot.start_time)
    : '';
  const starter = formenByCode[slot.division_code] ?? [];

  return (
    <div className={`adp-slot adp-slot--${status}`}>
      <div className="adp-slot-header">
        {label && <span className="adp-slot-time">{label}</span>}
        <span className="adp-slot-code">{slot.division_code}</span>
        <Verzug minuten={slot.verzug_minuten} planzeit={slot.start_time_plan} t={t} />
        {status === 'active' && <span className="adp-slot-active-dot" />}
        {starter.length === 0 && slot.participant_count > 0 && (
          <span className="adp-slot-count">{slot.participant_count} Tn.</span>
        )}
      </div>
      {starter.length > 0 && (
        <div className="adp-slot-starter">
          {starter.map((s, i) => (
            <div key={i} className="adp-slot-starter-row">
              <span className="adp-slot-starter-nr">{s.startnummer}.</span>
              <span className="adp-slot-starter-name">{s.vorname} {s.nachname}</span>
              {s.verein && <span className="adp-slot-starter-club">{s.verein}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tatami-Spalten-Body (scrollt mit) ────────────────────────────────────────
function MatteBody({ matte, running, ready, pending, zeitplan, formenByCode, turnierdatum, now, t }) {
  const nr = matte.nummer;
  const isLive     = running.some(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
  const readyHere  = ready.filter(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
  const pendingHere = [...pending.filter(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr))]
    .sort((a, b) => {
      if (a.geplante_zeit && b.geplante_zeit) return wanduhrzeit(a.geplante_zeit) - wanduhrzeit(b.geplante_zeit);
      return (a.kampf_nummer ?? 0) - (b.kampf_nummer ?? 0);
    })
    .slice(0, 4);

  const slotsHere = (zeitplan ?? [])
    .filter(s => Number(s.ring) === Number(nr))
    .filter(s => getSlotStatus(s, turnierdatum, now) !== 'done')
    .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''));

  const isEmpty = readyHere.length + pendingHere.length + slotsHere.length === 0;

  return (
    <div className={`adp-col-body${isEmpty ? ' adp-col-body--idle' : ''}`}>
      {isEmpty ? (
        <div className="adp-col-idle">
          <div className="adp-col-idle-kanji">{isLive ? '⚔' : '休'}</div>
          <div className="adp-col-idle-text">{isLive ? 'Kampf läuft' : 'Frei'}</div>
        </div>
      ) : (
        <>
          {readyHere.length > 0 && (
            <div className="adp-col-section adp-col-section--ready">
              <div className="adp-col-section-label adp-label--ready">{t('aufruf.ready')}</div>
              {readyHere.map(k => <KampfRow key={k.kampf_id} kampf={k} t={t} />)}
            </div>
          )}
          {pendingHere.length > 0 && (
            <div className="adp-col-section adp-col-section--pending">
              <div className="adp-col-section-label adp-label--pending">{t('aufruf.pending')}</div>
              {pendingHere.map(k => <KampfRow key={k.kampf_id} kampf={k} t={t} />)}
            </div>
          )}
          {slotsHere.length > 0 && (
            <div className="adp-col-section adp-col-section--zeitplan">
              <div className="adp-col-section-label adp-label--zeitplan">Zeitplan</div>
              {slotsHere.map(s => (
                <ZeitplanSlot key={s.id} slot={s} formenByCode={formenByCode} turnierdatum={turnierdatum} now={now} t={t} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Focus-Overlay: große Einzel-Ansicht ──────────────────────────────────────
function FocusOverlay({ item, formenByCode, turnierdatum, now, tatamis, t }) {
  if (!item) return null;
  const tatamiObj = tatamis.find(m => Number(m.nummer) === Number(item.tatami));
  const tatamiLabel = tatamiObj?.name && tatamiObj.name !== `Matte ${item.tatami}`
    ? tatamiObj.name
    : `${t('aufruf.mat')} ${item.tatami}`;

  if (item.type === 'kampf') {
    const k = item.data;
    return (
      <div className="adp-focus">
        <div className="adp-focus-tatami">{tatamiLabel}</div>
        <div className="adp-focus-category">{k.bracket_name}</div>
        <div className="adp-focus-round">{rundenName(k.runde, t)}</div>
        <div className="adp-focus-vs">
          <div className="adp-focus-fighter adp-focus-fighter--left">
            <div className="adp-focus-name">{k.k1_vorname} {k.k1_nachname}</div>
            <div className="adp-focus-club">{k.k1_verein || '—'}</div>
            {k.k1_gurt && <GurtDot farbe={k.k1_gurt} />}
          </div>
          <div className="adp-focus-badge">VS</div>
          <div className="adp-focus-fighter adp-focus-fighter--right">
            <div className="adp-focus-name">{k.k2_vorname} {k.k2_nachname}</div>
            <div className="adp-focus-club">{k.k2_verein || '—'}</div>
            {k.k2_gurt && <GurtDot farbe={k.k2_gurt} />}
          </div>
        </div>
        {k.geplante_zeit && (() => {
          const cd = calcCountdown(k.geplante_zeit, now);
          return cd ? <div className={`adp-focus-timer adp-col-timer--${cd.state}`}>{cd.text}</div> : null;
        })()}
      </div>
    );
  }

  // Formen/Zeitplan-Slot
  const slot = item.data;
  const starter = formenByCode[slot.division_code] ?? [];
  const label = slot.start_time
    ? (slot.end_time ? `${slot.start_time} – ${slot.end_time} Uhr` : `${slot.start_time} Uhr`)
    : '';
  const cd = slot.start_time ? calcCountdownToTime(slot.start_time, turnierdatum, now) : null;

  return (
    <div className="adp-focus adp-focus--slot">
      <div className="adp-focus-tatami">{tatamiLabel}</div>
      {label && <div className="adp-focus-time">{label}</div>}
      <Verzug minuten={slot.verzug_minuten} planzeit={slot.start_time_plan} t={t} gross />
      <div className="adp-focus-category">{slot.division_code}</div>
      {cd && <div className={`adp-focus-timer adp-col-timer--${cd.state}`}>{cd.text}</div>}
      {starter.length > 0 ? (
        <div className="adp-focus-starter-list">
          {starter.map((s, i) => (
            <div key={i} className="adp-focus-starter-row">
              <span className="adp-focus-starter-nr">{s.startnummer}.</span>
              <span className="adp-focus-starter-name">{s.vorname} {s.nachname}</span>
              {s.verein && <span className="adp-focus-starter-club">{s.verein}</span>}
            </div>
          ))}
        </div>
      ) : slot.participant_count > 0 ? (
        <div className="adp-focus-count">{slot.participant_count} Teilnehmer</div>
      ) : null}
    </div>
  );
}

// ── Hauptkomponente ──────────────────────────────────────────────────────────
export default function AufrufDisplayPage() {
  const { turnierId }         = useParams();
  const [searchParams]        = useSearchParams();
  const { t }                 = useTranslation();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [now, setNow]         = useState(Date.now());
  const timerRef              = useRef(null);

  // Sekundengenauer Tick
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Ping-Pong Auto-Scroll
  const viewportRef  = useRef(null);
  const contentRef   = useRef(null);
  const posRef       = useRef(0);
  const dirRef       = useRef(-1);
  const rafRef       = useRef(null);
  const SCROLL_SPEED = 0.5;
  const PAUSE_MS     = 2500;

  useEffect(() => {
    let pausing = false;
    const step = () => {
      const viewport = viewportRef.current;
      const content  = contentRef.current;
      if (!viewport || !content) { rafRef.current = requestAnimationFrame(step); return; }
      const maxScroll = content.scrollHeight - viewport.clientHeight;
      if (maxScroll <= 0) { rafRef.current = requestAnimationFrame(step); return; }
      posRef.current += dirRef.current * SCROLL_SPEED;
      if (posRef.current <= -maxScroll) {
        posRef.current = -maxScroll;
        if (!pausing) { pausing = true; setTimeout(() => { dirRef.current = 1; pausing = false; }, PAUSE_MS); }
      } else if (posRef.current >= 0) {
        posRef.current = 0;
        if (!pausing) { pausing = true; setTimeout(() => { dirRef.current = -1; pausing = false; }, PAUSE_MS); }
      }
      content.style.transform = `translateY(${posRef.current}px)`;
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Filter: optional ?matte=1,2,3
  const matteFilter = searchParams.get('matte')
    ? searchParams.get('matte').split(',').map(Number)
    : null;

  const load = useCallback(() => {
    fetch(`/api/brackets/turnier/${turnierId}/aufruf`)
      .then(r => r.json())
      .then(d => {
        if (d.success) { setData(d); setLastUpdate(new Date()); setError(null); }
        else setError('Daten konnten nicht geladen werden.');
      })
      .catch(() => setError('Verbindungsfehler.'))
      .finally(() => setLoading(false));
  }, [turnierId]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 20000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  // Schreiber-Call State pollen
  const [manualCall, setManualCall] = useState(null);
  useEffect(() => {
    const poll = () => {
      fetch(`/api/brackets/turnier/${turnierId}/aufruf-state`)
        .then(r => r.json())
        .then(d => setManualCall(d.state || null))
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [turnierId]);

  // Nächste bis zu 2 Kategorien für Focus-Cycling
  const turnierdatum = data?.turnier?.datum ?? null;

  const upcomingItems = useMemo(() => {
    if (!data) return [];
    const items = [];
    // Bereit-Kämpfe pro Tatami (max 1 pro Tatami)
    const seenTatami = new Set();
    for (const k of data.ready) {
      const tatami = k.tatami_nummer ?? k.matte_nummer;
      if (!tatami || seenTatami.has(tatami)) continue;
      seenTatami.add(tatami);
      items.push({ type: 'kampf', data: k, tatami, sortKey: `${String(tatami).padStart(3,'0')}_0` });
    }
    // Zeitplan-Slots (nicht erledigt, nach Tatami+Zeit)
    for (const s of (data.zeitplan ?? [])) {
      const status = getSlotStatus(s, turnierdatum, Date.now());
      if (status === 'done') continue;
      items.push({
        type: 'slot', data: s, tatami: s.ring,
        sortKey: `${String(s.ring).padStart(3,'0')}_${s.start_time ?? '99:99'}`
      });
    }
    items.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    return items.slice(0, 2);
  }, [data, turnierdatum]);

  const upcomingRef = useRef(upcomingItems);
  useEffect(() => { upcomingRef.current = upcomingItems; }, [upcomingItems]);

  // Focus-Phase Cycling: 0=Übersicht, 1=Item0, 2=Item1
  const [focusPhase, setFocusPhase] = useState(0);
  useEffect(() => {
    const duration = focusPhase === 0 ? 8000 : 6000;
    const timer = setTimeout(() => {
      const n = upcomingRef.current.length;
      setFocusPhase(p => {
        if (p === 0) return n > 0 ? 1 : 0;
        if (p === 1) return n > 1 ? 2 : 0;
        return 0;
      });
    }, duration);
    return () => clearTimeout(timer);
  }, [focusPhase]);

  // Welches Item im Focus zeigen?
  const focusItem = useMemo(() => {
    // Manueller Schreiber-Call hat Vorrang
    if (manualCall?.division_code) {
      const fromSlot = (data?.zeitplan ?? []).find(s => s.division_code === manualCall.division_code);
      if (fromSlot) return { type: 'slot', data: fromSlot, tatami: fromSlot.ring };
      const fromKampf = (data?.ready ?? []).find(k => k.division_code === manualCall.division_code)
                     || (data?.pending ?? []).find(k => k.division_code === manualCall.division_code);
      if (fromKampf) return { type: 'kampf', data: fromKampf, tatami: fromKampf.tatami_nummer ?? fromKampf.matte_nummer };
    }
    if (focusPhase === 0) return null;
    return upcomingItems[focusPhase - 1] ?? null;
  }, [manualCall, focusPhase, upcomingItems, data]);

  const showFocus = focusPhase !== 0 || manualCall !== null;

  // Tatami-Liste
  const tatamis = (() => {
    if (!data) return [];
    let list = [];
    if (data.kampfflaechen?.length) {
      list = matteFilter
        ? data.kampfflaechen.filter(kf => matteFilter.includes(kf.nummer))
        : data.kampfflaechen;
    } else {
      const zeitplanRings = (data.zeitplan ?? []).map(s => Number(s.ring)).filter(Boolean);
      const nrs = [...new Set([
        ...data.running.map(k => k.tatami_nummer ?? k.matte_nummer),
        ...data.ready.map(k => k.tatami_nummer ?? k.matte_nummer),
        ...data.pending.map(k => k.tatami_nummer ?? k.matte_nummer),
        ...zeitplanRings,
      ].filter(Boolean))].sort((a, b) => a - b);
      const filtered = matteFilter ? nrs.filter(n => matteFilter.includes(n)) : nrs;
      list = filtered.map(nr => ({ nummer: nr, name: null, farbe: null }));
    }
    const seen = new Set();
    return list.filter(kf => { if (seen.has(kf.nummer)) return false; seen.add(kf.nummer); return true; });
  })();

  // Formen-Starter gruppiert nach division_code
  const formenByCode = useMemo(() => {
    const map = {};
    for (const s of (data?.formenStarter ?? [])) {
      if (!map[s.division_code]) map[s.division_code] = [];
      map[s.division_code].push(s);
    }
    return map;
  }, [data]);

  // Header-Countdown pro Tatami
  function tatamiCountdown(nr) {
    if (!data) return null;
    const readyHere  = data.ready.filter(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
    const pendingHere = data.pending.filter(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
    const slotsHere  = (data.zeitplan ?? []).filter(s => Number(s.ring) === Number(nr));
    const nextFight  = (readyHere[0] || pendingHere[0])?.geplante_zeit ?? null;
    let cd = calcCountdown(nextFight, now);
    if (!cd) {
      const upcoming = slotsHere
        .filter(s => getSlotStatus(s, turnierdatum, now) === 'upcoming')
        .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''))[0];
      if (upcoming?.start_time) cd = calcCountdownToTime(upcoming.start_time, turnierdatum, now);
    }
    return cd;
  }

  const noMatteReady   = data?.ready.filter(k => !(k.tatami_nummer ?? k.matte_nummer)) ?? [];
  const noMattePending = data?.pending.filter(k => !(k.tatami_nummer ?? k.matte_nummer)).slice(0, 5) ?? [];

  if (loading) return (
    <div className="adp-page">
      <div className="adp-bg-kanji" aria-hidden>武</div>
      <div className="adp-center"><div className="adp-spinner" /><span>{t('aufruf.loading')}</span></div>
    </div>
  );

  if (error) return (
    <div className="adp-page">
      <div className="adp-center adp-error">⚠ {error}</div>
    </div>
  );

  const allEmpty = tatamis.length === 0 && noMatteReady.length === 0 && noMattePending.length === 0;

  return (
    <div className="adp-page">
      <div className="adp-bg-kanji" aria-hidden>武</div>
      <div className="adp-lang-switch"><LanguageSwitch /></div>

      <header className="adp-header">
        <div className="adp-header-org">TIGER &amp; DRAGON ASSOCIATION INTERNATIONAL</div>
        <h1 className="adp-header-title">KÄMPFER-AUFRUF</h1>
        {data?.turnier && <div className="adp-header-turnier">{data.turnier.name}</div>}
        <div className="adp-header-time">
          {t('aufruf.updated')}: {lastUpdate?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      </header>

      {/* Focus-Overlay (Schreiber-Call oder Auto-Cycle) */}
      {showFocus && focusItem && (
        <div className="adp-focus-wrap" onClick={() => { setFocusPhase(0); }}>
          <FocusOverlay
            item={focusItem}
            formenByCode={formenByCode}
            turnierdatum={turnierdatum}
            now={now}
            tatamis={tatamis}
            t={t}
          />
          <div className="adp-focus-hint">Klicken zum Schließen</div>
        </div>
      )}

      {/* ── Feste Header-Zeile (scrollt NICHT mit) ── */}
      {!allEmpty && (
        <div className="adp-headers-row">
          {tatamis.map(matte => {
            const nr = matte.nummer;
            const name = matte.name && matte.name !== `Matte ${nr}` ? matte.name : null;
            const isLive = data.running.some(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
            const cd = tatamiCountdown(nr);
            return (
              <div
                key={matte.kampfflaeche_id ?? nr}
                className={`adp-col-header${isLive ? ' adp-col-header--live' : ''}`}
                style={matte.farbe ? {'--matte-farbe': matte.farbe} : {}}
              >
                <span className="adp-col-num">{nr}</span>
                <div className="adp-col-title">
                  <span className="adp-col-mat-label">{name || t('aufruf.mat')}</span>
                  {isLive && <span className="adp-col-live-dot" />}
                </div>
                {cd && <div className={`adp-col-timer adp-col-timer--${cd.state}`}>{cd.text}</div>}
              </div>
            );
          })}
          {(noMatteReady.length > 0 || noMattePending.length > 0) && (
            <div className="adp-col-header adp-col-header--warteliste">
              <span className="adp-col-num">⚔</span>
              <div className="adp-col-title">
                <span className="adp-col-mat-label">{t('aufruf.pending')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Scrollbarer Board-Inhalt ── */}
      <div className="adp-board" ref={viewportRef}>
        {allEmpty ? (
          <div className="adp-empty">
            <div className="adp-empty-icon">⏸</div>
            <div>{t('aufruf.no_fights')}</div>
          </div>
        ) : (
          <div className="adp-board-inner" ref={contentRef}>
            {tatamis.map(matte => (
              <MatteBody
                key={matte.kampfflaeche_id ?? matte.nummer}
                matte={matte}
                running={data.running}
                ready={data.ready}
                pending={data.pending}
                zeitplan={data.zeitplan ?? []}
                formenByCode={formenByCode}
                turnierdatum={turnierdatum}
                now={now}
                t={t}
              />
            ))}

            {(noMatteReady.length > 0 || noMattePending.length > 0) && (
              <div className="adp-col-body adp-col-body--warteliste">
                {noMatteReady.map(k => (
                  <div key={k.kampf_id} className="adp-col-section adp-col-section--ready">
                    <div className="adp-col-section-label adp-label--ready">{t('aufruf.ready')}</div>
                    <KampfRow kampf={k} t={t} />
                  </div>
                ))}
                {noMattePending.map(k => (
                  <div key={k.kampf_id} className="adp-col-section adp-col-section--pending">
                    <KampfRow kampf={k} t={t} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <InfoLeiste info={data?.hallen_info} turnierId={turnierId} t={t} />
    </div>
  );
}
